import { useMemo } from "react";
import { Circle, Polygon, Tooltip } from "react-leaflet";

import { useGeofences } from "./useGeofences";
import { formatAreaMetres, polygonAreaAndPerimeterMetres } from "./geofencePolygon";
import { StackedGroup } from "./LayerStack";
import { LAYER_RANK } from "./layerStackOrder";
import { fencePathOptions } from "./mapStyles";

// Note: matches LocationType.Pit in the backend's Helpers/Enums.cs. The frontend has no shared enum for
// this - every other location-type check in the app (e.g. GeologyRehandle's dump/crusher check) also
// compares against the raw id.
const PIT_LOCATION_TYPE_ID = 7;

/**
 * Draws the geofences a machine's position is actually matched against, coloured by location type, and a
 * stockpile by the grade it holds.
 *
 * Note: this is the map explaining itself. Without it a machine sitting 20 m outside a fence looks
 * identical to one sitting inside it, and "why is that truck not on the crusher" has no visible answer.
 */

type GeofenceLayerProps = {
  /** Note: hidden by default is wrong; this is the map's own reference frame. Toggling is for decluttering. */
  visible: boolean;
  /**
   * Note: the fence currently being drawn in the editor. It is excluded so the saved shape is not drawn
   * underneath the editable one, where two nearly-coincident outlines read as a rendering fault.
   */
  excludeLocationId?: number;
};

export const GeofenceLayer: React.FC<GeofenceLayerProps> = ({ visible, excludeLocationId }) => {
  const { fences } = useGeofences();

  const shown = useMemo(
    () => (excludeLocationId == null ? fences : fences.filter(f => f.id !== excludeLocationId))
      .map(fence => ({ ...fence, pathOptions: fencePathOptions(fence.locationTypeId, fence.stockpileType) })),
    [fences, excludeLocationId]);

  if (!visible) return null;

  return (
    <StackedGroup rank={LAYER_RANK.fences} version={shown}>
      {shown.map(fence => fence.kind === "circle" ? (
        <Circle
          key={`fence-${fence.id}`}
          center={fence.centre}
          radius={fence.radius}
          pathOptions={fence.pathOptions}
        >
          <Tooltip>{fence.name} - {fence.radius} m radius{fence.buffer > 0 ? ` +${fence.buffer} m buffer` : ""}</Tooltip>
        </Circle>
      ) : (
        <Polygon
          key={`fence-${fence.id}`}
          positions={fence.positions}
          pathOptions={fence.pathOptions}
        >
          <Tooltip>
            {fence.name}
            {fence.locationTypeId === PIT_LOCATION_TYPE_ID && (() => {
              const measurements = polygonAreaAndPerimeterMetres(fence.positions.map(([lat, lng]) => ({ lat, lng })));
              return measurements ? ` - ${formatAreaMetres(measurements.areaSqM)}` : "";
            })()}
            {fence.buffer > 0 ? ` +${fence.buffer} m buffer` : ""}
          </Tooltip>
        </Polygon>
      ))}

      {/* Note: the BUFFER is drawn for circles and not for polygons, and that asymmetry is deliberate
          rather than unfinished. A circle's buffer is just a larger radius; a polygon's is an outward
          offset of every edge, which needs real polygon-buffering geometry and would be a lot of code to
          draw a hint. The tooltip states the buffer in both cases so the number is never hidden. */}
      {shown.map(fence => fence.kind === "circle" && fence.buffer > 0 ? (
        <Circle
          key={`buffer-${fence.id}`}
          center={fence.centre}
          radius={fence.radius + fence.buffer}
          pathOptions={{ color: fence.pathOptions.color, weight: 1, opacity: 0.4, fill: false, dashArray: "4 6" }}
          interactive={false}
        />
      ) : null)}
    </StackedGroup>
  );
};

export default GeofenceLayer;
